import React, { useState, useMemo } from 'react';
import { formatCurrencySigFig } from '../../utils/formatters';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { useFormContextOptional } from '../../contexts/FormContext';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import ExVesselValueModal from '../ExVesselValueModal/ExVesselValueModal';
import InsightColumnPair from '../InsightColumnPair';
import './LandedExVesselVal.css';

function filterExvesselByYear(
  exvessel: unknown[] | undefined,
  selectedYear: string
): unknown[] {
  if (!exvessel || !Array.isArray(exvessel)) return [];
  const withYear = (item: unknown): number => (item as { year?: number })?.year ?? 0;
  if (selectedYear === 'historical') return exvessel.filter((item) => withYear(item) <= 2024);
  if (selectedYear === 'projections') return exvessel.filter((item) => withYear(item) >= 2025);
  return exvessel;
}

interface InsightData {
  exVesselValue?: number;
  exVesselUncertainty?: number;
}

interface LandedExVesselValData {
  insights?: InsightData;
  originalInsights?: InsightData;
  parametersChanged?: boolean;
  stockName?: string;
  onOpenExVesselModal?: () => void;
}

function getDescription(stockName?: string): string {
  const base =
    'when they first land (sell) it at the dock, before any processing, distribution, or retail markups. Landings (in weight) × Price per unit weight (ex-vessel price)';
  if (!stockName || stockName === 'All Stocks') {
    return `The price that commercial fishers receive for their catch from all stocks ${base}`;
  }
  return `The price that commercial fishers receive for their catch from ${stockName} ${base}`;
}

export default function LandedExVesselVal(props: RegistryComponentProps): React.ReactElement {
  const formContext = useFormContextOptional();
  const pageData = usePageDataContextOptional();
  const injectionData = getRegistryData<LandedExVesselValData>(props);

  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const pageInsights = pageData?.data?.insights as InsightData | undefined;
  const pageOriginalInsights = pageData?.data?.originalInsights as InsightData | undefined;
  const pageStockName = pageData?.data?.stockName as string | undefined;
  const pageTimeSeriesData = pageData?.data?.timeSeriesData as { exvessel?: unknown[] } | undefined;
  const pageLoading = pageData?.loading as boolean | undefined;
  const pageError = pageData?.error as string | null | undefined;
  const selectedModel = pageData?.data?.selectedModel as string | undefined;

  const insights = injectionData.insights ?? pageInsights ?? {};
  const originalInsights = injectionData.originalInsights ?? pageOriginalInsights;
  const parametersChanged = (injectionData.parametersChanged ?? pageData?.data?.parametersChanged) ?? false;
  const stockName = injectionData.stockName ?? pageStockName;

  const selectedYear = (formContext?.values?.['time-period'] as string) ?? 'projections';
  const filteredExvessel = useMemo(
    () => filterExvesselByYear(pageTimeSeriesData?.exvessel, selectedYear),
    [pageTimeSeriesData?.exvessel, selectedYear]
  );
  const canOpenModal = selectedModel === 'WHAM';
  const onOpenExVesselModal = canOpenModal ? () => setShowModal(true) : undefined;

  const handleCloseModal = () => {
    setShowModal(false);
    document.body.style.overflow = 'auto';
  };
  const showSingleColumn = selectedYear === 'historical';
  const showStatusQuoColumn = !showSingleColumn;
  const showChangeIndicators =
    !showSingleColumn && !!originalInsights && parametersChanged;

  const statusQuoDisplay = originalInsights ?? insights;
  const description = getDescription(stockName);

  return (
    <div className="landed-ex-vessel-val insight-card">
      <div className="insight-title-row">
        <h3>Landed Ex-vessel Value</h3>
        <button
          className="info-icon"
          onClick={() => setExpanded((e) => !e)}
          aria-label="Toggle description"
          type="button"
        >
          i
        </button>
      </div>
      <div className={`insight-subtitle-container ${expanded ? 'expanded' : ''}`}>
        <h2 className="insight-subtitle" dangerouslySetInnerHTML={{ __html: description }} />
      </div>
      <InsightColumnPair
        originalValue={originalInsights?.exVesselValue}
        currentValue={insights.exVesselValue}
        statusQuoContent={
          <>
            {formatCurrencySigFig(statusQuoDisplay.exVesselValue)}
            <div className="uncertainty-value">
              (± {formatCurrencySigFig(statusQuoDisplay.exVesselUncertainty, false)})
            </div>
          </>
        }
        currentContent={
          <>
            {formatCurrencySigFig(insights.exVesselValue)}
            <div className="uncertainty-value">
              (± {formatCurrencySigFig(insights.exVesselUncertainty, false)})
            </div>
          </>
        }
        showStatusQuoColumn={showStatusQuoColumn}
        showSingleColumn={showSingleColumn}
        showChangeIndicators={Boolean(showChangeIndicators)}
        parametersChanged={Boolean(parametersChanged)}
        onClick={onOpenExVesselModal}
        clickable={Boolean(onOpenExVesselModal)}
        isLoading={Boolean(pageLoading)}
        errorMessage={pageError}
      />

      <ExVesselValueModal
        isOpen={showModal}
        onClose={handleCloseModal}
        data={filteredExvessel}
        loading={pageLoading}
        error={pageError}
        stockName={stockName}
      />
    </div>
  );
}
